import type { Member, Channel, Role, Punishment } from '../types';
import { apiClient } from './client';

interface CreatePunishmentRequest {
  userId: string;
  type: 'warn' | 'timeout' | 'kick' | 'ban';
  reason: string;
  duration?: number;
}

interface GetPunishmentsParams {
  userId?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

interface GetPunishmentsResponse {
  punishments: Punishment[];
  total: number;
  page: number;
  limit: number;
}

interface CancelPunishmentRequest {
  reason: string;
}

export const punishmentsApi = {
  getMembers: async (guildId: string, query?: string): Promise<Member[]> => {
    const response = await apiClient.get<Member[]>(`/guilds/${guildId}/members`, {
      params: { query },
    });
    return response.data;
  },

  getChannels: async (guildId: string): Promise<Channel[]> => {
    const response = await apiClient.get<Channel[]>(`/guilds/${guildId}/channels`);
    return response.data;
  },

  getRoles: async (guildId: string): Promise<Role[]> => {
    const response = await apiClient.get<Role[]>(`/guilds/${guildId}/roles`);
    return response.data;
  },

  createPunishment: async (guildId: string, data: CreatePunishmentRequest): Promise<void> => {
    await apiClient.post(`/guilds/${guildId}/punishments`, data);
  },

  getPunishments: async (
    guildId: string,
    params?: GetPunishmentsParams
  ): Promise<GetPunishmentsResponse> => {
    const response = await apiClient.get<any>(
      `/guilds/${guildId}/punishments`,
      { params }
    );
    
    // API returns { data: [...], total, page, limit }
    // but we need { punishments: [...], total, page, limit }
    const responseData = response.data;
    return {
      punishments: Array.isArray(responseData.data) ? responseData.data : [],
      total: typeof responseData.total === 'number' ? responseData.total : 0,
      page: typeof responseData.page === 'number' ? responseData.page : 1,
      limit: typeof responseData.limit === 'number' ? responseData.limit : 10,
    };
  },

  cancelPunishment: async (
    guildId: string,
    punishmentId: string,
    data: CancelPunishmentRequest
  ): Promise<void> => {
    await apiClient.patch(`/guilds/${guildId}/punishments/${punishmentId}/cancel`, data);
  },
};
